import ImageLayer from 'ol/layer/Image'
import ImageWMS from 'ol/source/ImageWMS'
import TileLayer from 'ol/layer/Tile'
import TileWMS from 'ol/source/TileWMS'
import TileGrid from 'ol/tilegrid/TileGrid'
import {get as getProjection} from 'ol/proj'
import {getWidth} from 'ol/extent'
import * as XLSX from "xlsx-js-style";
import JSZip from 'jszip'

// Used in MMP.vue
const createLayers = (objArray, map) => {
  let layersObj = {}

  const projExtent = getProjection('EPSG:3857').getExtent()
  const startResolution = getWidth(projExtent) / 512
  const resolutions = new Array(28)
  for (let i = 0, ii = resolutions.length; i < ii; ++i) {
    resolutions[i] = startResolution / Math.pow(2, i)
  }
  const tileGrid = new TileGrid({
    extent: [
      -20037508.3427892, -20037508.3427892, 20037508.3427892, 20037508.3427892
    ],
    resolutions: resolutions,
    tileSize: [512, 512]
  })

  for (let i in objArray) {
    let currentObj = objArray[i]  
    
    currentObj.params = 
      {
         FORMAT:'image/png8',
         LAYERS:currentObj.params.LAYERS,
         authkey:sessionStorage.token
      }

    let currentLayer
    let geoserverNameWorkspace

    switch (currentObj.layerType) {
      case 'ImageWMS':
      case 'XYZ':
        currentLayer = new ImageLayer({
          minZoom: currentObj.minZoom ? currentObj.minZoom : undefined,
          source: new ImageWMS({
            url: currentObj.url,
            params: currentObj.params,
            serverType: 'geoserver',
            transition: 0,
            crossOrigin: 'anonymous'
          }),
          name: currentObj.name,
          zIndex: currentObj.zIndex
        })
        geoserverNameWorkspace = currentLayer.values_.source.url_
        break
      case 'TileWMS512':
        currentLayer = new TileLayer({
          minZoom: currentObj.minZoom ? currentObj.minZoom : undefined,
          source: new TileWMS({
            url: currentObj.url,
            params: currentObj.params,
            serverType: 'geoserver',
            tileGrid: tileGrid,
            transition: 0,
            gridSet: 'WebMercatorQuadx2',
            crossOrigin: 'anonymous'
          }),
          name: currentObj.name,
          zIndex: currentObj.zIndex
        })
        geoserverNameWorkspace = currentLayer.values_.source.urls[0]
        break
    }

    layersObj[currentObj.name] = {
      layer: currentLayer,
      zoomControl: currentObj.zoomControl,
      geoserverName: geoserverNameWorkspace,
      type: currentObj.layerType,
      fields: currentObj.fields,
      minZoom: currentObj.minZoom
    }

    if (!currentObj.zoomControl && map) {
      map.addLayer(currentLayer)
    }
  }
  return layersObj
}

// Used in MMP_ANSI_Layers.vue and MMP_DRT_Layers.vue
const orderMapLayers = (layersObj, layers, map) => {
  var mapLayers = map.getLayers().getArray()
  let orderSegments = layers.map(i => {
    return i.name
  })
  orderSegments = orderSegments.reverse()

  for (var i = 0; i < orderSegments.length; i++) {
    var currentIndex = i + 500000
    for (var j = 0; j < mapLayers.length; j++) {
      if (layersObj[orderSegments[i]].layer == mapLayers[j]) {
        mapLayers[j].setZIndex(currentIndex)
      }
    }
  }
}

// Modifying default function for later functions
Number.prototype.pad = function (size) {
  var s = String(this)
  while (s.length < (size || 2)) {
    s = '0' + s
  }
  return s
}

// Used in MMP.vue and MMP_DPR_EntryManager.vue
function addDays(date, days) {
  const newDate = new Date(Number(date))
  newDate.setDate(newDate.getDate() + days) 
  return (
    newDate.getFullYear() +
    '-' +
    (newDate.getMonth() + 1).pad(2) +
    '-' +
    newDate.getDate().pad(2)
  )
}

function getDateStringForFileName() {
  const now = new Date()
  const yyyy = now.getFullYear()
  const mm = String(now.getMonth() + 1).padStart(2, '0')
  const dd = String(now.getDate()).padStart(2, '0')
  const hh = String(now.getHours()).padStart(2, '0')
  const min = String(now.getMinutes()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}_${hh}:${min}`
}

/**
 * Style a worksheet professionally for Excel exports
 * @param {Worksheet} ws - SheetJS worksheet (from XLSX.utils.*)
 * @param {Object} opts
 * @param {Boolean} opts.leftAlignNumbers - force numbers to left by using text format (default: false)
 * @param {Number[]} opts.boldCols - 0-based column indexes to bold
 * @param {Number[]} opts.boldRows - 0-based row indexes to bold
 */
function styleWorksheet(ws, opts = {}) {
  const { leftAlignNumbers = false, boldCols = [], boldRows = [] } = opts;

  if (!ws || !ws["!ref"]) return;
  const range = XLSX.utils.decode_range(ws["!ref"]);
  const colWidths = [];

  for (let R = range.s.r; R <= range.e.r; ++R) {
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const cellRef = XLSX.utils.encode_cell({ r: R, c: C });
      const cell = ws[cellRef];
      if (!cell) continue;

      const isHeader = R === range.s.r;
      const makeBold = boldCols.includes(C) || boldRows.includes(R);

      // alignment rules
      let hAlign = "left";
      if (!leftAlignNumbers && cell.t === "n" && !isHeader) hAlign = "right";
      if (leftAlignNumbers && cell.t === "n") {
        cell.z = "@"; // text format to allow left align
      }

      // Apply style
      cell.s = {
        font: { bold: !!makeBold },
        alignment: { horizontal: hAlign, vertical: "center", wrapText: true },
        border: {
          top:    { style: "thin", color: { rgb: "000000" } },
          bottom: { style: "thin", color: { rgb: "000000" } },
          left:   { style: "thin", color: { rgb: "000000" } },
          right:  { style: "thin", color: { rgb: "000000" } }
        }
      };

      // compute content length
      const cellValue = cell.v == null ? "" : String(cell.v);
      const length = cellValue.length;

      // first column = full length, others = half
      const targetWidth = C === 0 ? length + 2 : Math.ceil((length + 2) / 4);

      colWidths[C] = Math.max(colWidths[C] || 10, targetWidth);
    }
  }

  ws["!cols"] = colWidths.map(w => ({ wch: w }));
}

/***
 * Create a Random 8 character string of lowercase letters, uppercase letters, digits and special characters.
 * Used for creating temporary passwords
 * @param {Worksheet} ws
 * @returns a random string of 8 characters
 */
function generateSecureString(outputStringLength = 12) {
  if (outputStringLength < 4) {
    throw new Error("Length must be at least 4 to include required character types.");
  }

  const lower = 'abcdefghijklmnopqrstuvwxyz';
  const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const digits = '0123456789';
  const safeSpecial = '-_~';

  const all = lower + upper + digits + safeSpecial;

  // Secure random picker
  const pick = (chars) => {
    const arr = new Uint32Array(1);
    crypto.getRandomValues(arr);
    return chars[arr[0] % chars.length];
  };

  // Ensure minimum character requirements
  let result = [
    pick(lower),
    pick(upper),
    pick(digits),
    pick(safeSpecial)
  ];

  // Fill remaining characters
  for (let i = result.length; i < outputStringLength; i++) {
    result.push(pick(all));
  }

  // Secure shuffle (Fisher-Yates + crypto)
  for (let i = result.length - 1; i > 0; i--) {
    const arr = new Uint32Array(1);
    crypto.getRandomValues(arr);
    const j = arr[0] % (i + 1);
    [result[i], result[j]] = [result[j], result[i]];
  }

  return result.join('');
}

/**
 * Create a current date string 
 * format MM-dd-YYYY
 */
function getFormattedDate() {
  const today = new Date()
  const dd = String(today.getDate()).padStart(2, '0')
  const mm = String(today.getMonth() + 1).padStart(2, '0')
  const yyyy = today.getFullYear()
  return `${mm}-${dd}-${yyyy}`
}

/**
 * Create a current date time string 
 * format MM-dd-YYYY_hh:mm:ss
 */
function getFormattedDateTime() {
  const now = new Date()

  const dd = String(now.getDate()).padStart(2, '0')
  const mm = String(now.getMonth() + 1).padStart(2, '0')
  const yyyy = now.getFullYear()

  const hh = String(now.getHours()).padStart(2, '0')
  const min = String(now.getMinutes()).padStart(2, '0')
  const ss = String(now.getSeconds()).padStart(2, '0')

  return `${mm}-${dd}-${yyyy}_${hh}-${min}-${ss}`
}

/**
 * Extract KML from KMZ zip file
 * @param {*} file 
 * @returns kml string
 */
async function getKmlFromKmz(file) {
  const allKmls = await extractAllKmlTexts(file);
  const combinedKmlText = mergeAllKmlTexts(allKmls);
  return combinedKmlText;
}

/**
 * Function to remove any named add-hoc Layer from the map object 
 * @returns Remove Custom Layers
 */
function removeCustomLayers(map, layerName) {
  if (!map) return

  const mapLayers = map.getLayers().getArray().slice() // clone

  mapLayers.forEach(layer => {
    if (layer && layer.get('name') === layerName) {

      // clear source features
      const source = layer.getSource && layer.getSource()
      if (source) {
        source.clear(true)
      }

      // remove from map
      map.removeLayer(layer)
    }
  })
}


/**
 * Recursively extract all KMLs from KMZ (supports nested KMZs)
 */
async function extractAllKmlTexts(fileOrBuffer, basePath = "") {
  const zip =
    fileOrBuffer instanceof ArrayBuffer
      ? await JSZip.loadAsync(fileOrBuffer)
      : await JSZip.loadAsync(await fileOrBuffer.arrayBuffer());

  const kmlTexts = [];

  for (const [path, entry] of Object.entries(zip.files)) {
    if (entry.dir) continue;

    const fullPath = `${basePath}${path}`;

    // Nested KMZ
    if (path.toLowerCase().endsWith(".kmz")) {
      const nestedBuffer = await entry.async("arraybuffer");
      const nestedKmls = await extractAllKmlTexts(
        nestedBuffer,
        `${fullPath}/`
      );
      kmlTexts.push(...nestedKmls);
    }

    // KML
    if (path.toLowerCase().endsWith(".kml")) {
      const text = await entry.async("text");
      kmlTexts.push({
        path: fullPath,
        text
      });
    }
  }

  return kmlTexts;
}

/**
 * Combine all KML texts into a single KML document with folders
 */
export function mergeAllKmlTexts(kmlEntries) {
  const parser = new DOMParser();
  const serializer = new XMLSerializer();

  const mergedDoc = parser.parseFromString(
    `<?xml version="1.0" encoding="UTF-8"?>
     <kml xmlns="http://www.opengis.net/kml/2.2">
       <Document></Document>
     </kml>`,
    "text/xml"
  );

  const rootDocument = mergedDoc.getElementsByTagName("Document")[0];

  for (const { path, text } of kmlEntries) {
    const doc = parser.parseFromString(text, "text/xml");
    const placemarks = doc.getElementsByTagName("Placemark");

    if (!placemarks.length) continue;

    const folder = mergedDoc.createElement("Folder");

    const name = mergedDoc.createElement("name");
    name.textContent = path;
    folder.appendChild(name);

    for (let i = 0; i < placemarks.length; i++) {
      folder.appendChild(placemarks[i].cloneNode(true));
    }

    rootDocument.appendChild(folder);
  }

  return serializer.serializeToString(mergedDoc);
}

export {createLayers, orderMapLayers, addDays, getDateStringForFileName, styleWorksheet, generateSecureString, getFormattedDate, getKmlFromKmz, removeCustomLayers, getFormattedDateTime}
